/**
 * Opt-in features — the beta features a person can turn on for themselves.
 *
 * Same shape as Tours: cards in a collapsible section per BU. The difference worth knowing is
 * what "ativa" means here — it is a kill switch, not a draft flag. A feature that is off leaves
 * every bootstrap on the next load, which puts everyone back on the old behaviour immediately.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteOptInFeature } from '../../Hooks/useDeleteOptInFeature';
import { useOptInFeatures } from '../../Hooks/useOptInFeatures';
import { useRestoreOptInFeature } from '../../Hooks/useRestoreOptInFeature';
import type { OptInFeature } from '../../Types/OptInFeatures.types';
import { groupFeaturesByTenant } from '../../Utils/groupFeaturesByTenant';
import { OptInFeatureCard } from '../OptInFeatureCard/OptInFeatureCard';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useTenants } from '@/Hooks/useTenants';
import { getTenantColor, getTenantIcon } from '@/Shared/brand';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { ConfirmationDialog } from '@/Shared/components/ConfirmationDialog/ConfirmationDialog';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { Switch } from '@/Shared/components/Switch/Switch';
import { TruncatedListNotice } from '@/Shared/components/TruncatedListNotice/TruncatedListNotice';
import { isListTruncated } from '@/Shared/constants/listLimits';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const filterStyle = css`
  min-width: 200px;
`;

const groupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const errorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-ml);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
`;

const errorTitleStyle = css`
  font-weight: var(--font-weight-semibold);
`;

/** Matches the card footer, which carries a labelled button next to two icon buttons. */
const cardsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: var(--space-base);
`;

/**
 * The "mostrar excluídas" control.
 *
 * A toggle inside the same list, not a tab: `includeDeleted` ADDS the deleted rows to the live
 * ones, and that is the point. A deleted feature is on screen because it is still holding its key
 * — `@@unique([tenantId, key])` does not look at `deletedAt` — and a key being held only means
 * something next to the features using theirs. A separate "Excluídas" tab would answer a question
 * nobody asked and hide the one that matters.
 */
const showDeletedStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const ALL_TENANTS = 'all';

const OptInFeaturesList = (): JSX.Element => {
  const navigate = useNavigate();
  const { pages: privileges } = usePrivileges();
  const { tenants, tenantOptions } = useTenants();

  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<OptInFeature | undefined>();
  const [showDeleted, setShowDeleted] = useState(false);

  const { data, isLoading, error } = useOptInFeatures(showDeleted ? { includeDeleted: true } : {});
  const deleteFeature = useDeleteOptInFeature();
  const restoreFeature = useRestoreOptInFeature();

  const allFeatures = useMemo(() => data?.data ?? [], [data]);

  const groups = useMemo(() => {
    const scoped =
      tenantFilter === ALL_TENANTS
        ? allFeatures
        : allFeatures.filter(feature => feature.tenantSlug === tenantFilter);

    return groupFeaturesByTenant(scoped);
  }, [allFeatures, tenantFilter]);

  const filterOptions = useMemo(
    () => [
      { id: ALL_TENANTS, text: 'Todos os tenants', value: ALL_TENANTS },
      ...tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    ],
    [tenantOptions],
  );

  if (isLoading) {
    return (
      <MainContent id='opt-in-features-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='opt-in-features-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Funcionalidades em beta que cada pessoa liga para si. Desativar aqui é um kill switch:
            na próxima carga do dashboard todo mundo volta ao comportamento antigo, sem perder o
            estado de quem já tinha ligado.
          </p>
          <div className={spacerStyle} />
          <div className={filterStyle}>
            <UTSelect
              options={filterOptions}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[filterOptions[0]]}
              inputOptions={{
                textFieldProps: { placeholder: 'Todos os tenants' },
              }}
              onSelectedOptionsChange={options => {
                setTenantFilter(options.length > 0 ? String(options[0].value) : ALL_TENANTS);
              }}
            />
          </div>

          <span className={showDeletedStyle}>
            <Switch
              id='opt-in-features-show-deleted'
              checked={showDeleted}
              aria-label='Mostrar funcionalidades excluídas'
              onChange={setShowDeleted}
            />
            {/* Sibling, not wrapper: `Switch` renders its own label around the input. */}
            <label htmlFor='opt-in-features-show-deleted'>Mostrar excluídas</label>
          </span>

          {isListTruncated(data?.pagination.total, allFeatures.length) ? (
            <TruncatedListNotice
              id='opt-in-features-truncated'
              shown={allFeatures.length}
              total={data?.pagination.total ?? allFeatures.length}
              noun='funcionalidades'
            />
          ) : null}
          {privileges.BUSINESS_UNITS.OPT_IN_FEATURES.CREATE ? (
            <UTButton
              fitContent
              id='opt-in-features-create'
              text='Nova funcionalidade'
              icon='Arrows.Plus'
              onClick={() => {
                navigate('/opt-in-features/new');
              }}
            />
          ) : null}
        </div>

        {error ? (
          <div id='opt-in-features-error' className={errorStyle} role='alert'>
            <span className={errorTitleStyle}>Não foi possível carregar as funcionalidades</span>
            <span>{error.message}</span>
          </div>
        ) : null}

        {!error && groups.length === 0 ? (
          <EmptyState
            id='opt-in-features-empty'
            icon='Misc.Lightbulb'
            title='Nenhuma funcionalidade opt-in cadastrada'
            description='Cadastre uma funcionalidade beta para que as pessoas possam ligá-la por conta própria no dashboard — e para acompanhar quem ligou, quem voltou atrás e por quê.'
            action={
              privileges.BUSINESS_UNITS.OPT_IN_FEATURES.CREATE ? (
                <UTButton
                  fitContent
                  text='Nova funcionalidade'
                  icon='Arrows.Plus'
                  onClick={() => {
                    navigate('/opt-in-features/new');
                  }}
                />
              ) : undefined
            }
          />
        ) : null}

        {!error && groups.length > 0 ? (
          <div className={groupsStyle}>
            {groups.map(group => {
              const tenant = tenants.find(item => item.slug === group.tenantSlug);

              return (
                <CollapsibleSection
                  key={group.tenantSlug}
                  id={`opt-in-features-section-${group.tenantSlug}`}
                  title={group.tenantSlug}
                  monoTitle
                  icon={getTenantIcon({
                    slug: group.tenantSlug,
                    iconName: tenant?.iconName,
                  })}
                  iconColor={getTenantColor(group.tenantSlug)}
                  count={group.countLabel}
                  summary={group.activeLabel}
                  open={!collapsed[group.tenantSlug]}
                  onToggle={() => {
                    setCollapsed(current => ({
                      ...current,
                      [group.tenantSlug]: !current[group.tenantSlug],
                    }));
                  }}
                >
                  <div className={cardsGridStyle}>
                    {group.features.map(feature => (
                      <OptInFeatureCard
                        key={feature.id}
                        feature={feature}
                        canEdit={privileges.BUSINESS_UNITS.OPT_IN_FEATURES.EDIT}
                        canDelete={privileges.BUSINESS_UNITS.OPT_IN_FEATURES.DELETE}
                        onAdoption={target => {
                          navigate(`/opt-in-features/${target.id}/adoption`);
                        }}
                        onEdit={target => {
                          navigate(`/opt-in-features/${target.id}`);
                        }}
                        onDelete={setDeleting}
                        /*
                          Restoring rides on `opt_in_feature:update`, not on a permission of its
                          own: whoever can delete can undo their own mistake.
                        */
                        onRestore={
                          privileges.BUSINESS_UNITS.OPT_IN_FEATURES.EDIT
                            ? target => {
                                restoreFeature.mutate(target.id);
                              }
                            : undefined
                        }
                        isRestoring={
                          restoreFeature.isPending && restoreFeature.variables === feature.id
                        }
                      />
                    ))}
                  </div>
                </CollapsibleSection>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {deleting ? (
        <ConfirmationDialog
          open
          title='Excluir funcionalidade'
          message={`"${deleting.name}" sai do painel e do bootstrap. Quem estava com ela ligada volta ao comportamento antigo. O histórico de adoção continua guardado.`}
          confirmText='Excluir'
          cancelText='Cancelar'
          variant='danger'
          isLoading={deleteFeature.isPending}
          onClose={() => {
            setDeleting(undefined);
          }}
          onConfirm={() => {
            deleteFeature.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(undefined);
              },
            });
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { OptInFeaturesList };
